import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react'
import { makeStyles } from '@material-ui/styles'
import Box from '@material-ui/core/Box'
import { observer } from 'mobx-react-lite'
import Chart from 'chart.js'
import { pull } from 'lodash'
import { format } from 'date-fns'
import useStore from 'hooks/useStore'

Chart.defaults.global.defaultFontColor = '#333'
Chart.defaults.global.defaultFontFamily = '"Noto Sans KR", sans-serif'
Chart.defaults.global.defaultFontSize = 11

const backgroundColorSet = [
	'rgba(255, 99, 132, 0.2)',
	'rgba(54, 162, 235, 0.2)',
	'rgba(255, 206, 86, 0.2)',
	'rgba(75, 192, 192, 0.2)',
	'rgba(153, 102, 255, 0.2)',
	'rgba(255, 159, 64, 0.2)',
	'rgba(255, 99, 132, 0.2)',
	'rgba(54, 162, 235, 0.2)',
	'rgba(255, 206, 86, 0.2)',
]
const borderColorSet = [
	'rgba(255, 99, 132, 1)',
	'rgba(54, 162, 235, 1)',
	'rgba(255, 206, 86, 1)',
	'rgba(75, 192, 192, 1)',
	'rgba(153, 102, 255, 1)',
	'rgba(255, 159, 64, 1)',
	'rgba(255, 99, 132, 1)',
	'rgba(54, 162, 235, 1)',
	'rgba(255, 206, 86, 1)',
]

const useStyles = makeStyles(theme => ({
	chart: {
		'& canvas': {
			width: '100% !important',
			height: '100% !important',
		},
		position: 'relative',
		width: '100%',
		[theme.breakpoints.up('md')]: {
			height: '100%',
			flex: 1,
		},
		[theme.breakpoints.down('sm')]: {
			height: 550,
		},
	},
}))

function ClapChart() {
	const classes = useStyles()
	const store = useStore()

	const ctxMount = useRef(null)
	const canvasEl = useRef(null)
	const [tooltip, setTooltip] = useState({ className: '', style: {}, body: '' })

	const customTooltips = useCallback(tt => {
		// Hide if no tooltip
		if (tt.opacity === 0) {
			setTooltip(tooltip => ({ ...tooltip, style: { ...tooltip.style, opacity: 0 } }))
			return
		}

		// Set Text
		let body = ''
		if (tt.body) {
			let bodyItems = tt.body
				.map(bodyItem => bodyItem.lines[0])
				.filter(x => {
					let temp = x.split(':'),
						count = Number(temp[1])
					return count
				})
			body = bodyItems.join('<br/>').replace(/:/g, ' :')
		}

		// Set Style
		let positionY = ctxMount.current.canvas.offsetTop,
			positionX = ctxMount.current.canvas.offsetLeft

		// for stack
		let points = tt.dataPoints,
			x = points[points.length - 1].x

		let style = {
			opacity: 1,
			position: 'absolute',
			left: positionX + x + 5,
			top: positionY + tt.caretY - 14,
			fontFamily: tt._bodyFontFamily,
			fontSize: tt.bodyFontSize,
			fontStyle: tt._bodyFontStyle,
			padding: `${tt.yPadding + 2}px ${tt.xPadding + 2}px`,
			backgroundColor: 'rgba(0, 0, 0, 0.5)',
			color: '#fff',
			borderRadius: 5,
		}

		setTooltip(tooltip => {
			// Set caret Position
			let classList = tooltip.className.split(' ')
			pull(classList, 'above', 'below', 'no-transform')
			if (tt.yAlign) {
				!classList.includes(tt.yAlign) && classList.push(tt.yAlign)
			} else {
				!classList.includes('no-transform') && classList.push('no-transform')
			}
			return { className: classList.join(' '), style, body }
		})
	}, [])

	useEffect(() => {
		if (!ctxMount.current) {
			const ctx = canvasEl.current.getContext('2d')
			ctxMount.current = new Chart(ctx, {
				type: 'horizontalBar',
				data: {
					labels: Array.from({ length: 24 }, (v, i) => i + '시'),
					datasets: [
						{
							label: '박수',
							data: store.count.clap,
							backgroundColor: ({ dataIndex }) => backgroundColorSet[dataIndex % 6],
							borderColor: ({ dataIndex }) => borderColorSet[dataIndex % 6],
							borderWidth: 1,
						},
						{
							label: '메시지',
							data: store.count.message,
							backgroundColor: ({ dataIndex }) => backgroundColorSet[(dataIndex % 6) + 3],
							borderColor: ({ dataIndex }) => borderColorSet[(dataIndex % 6) + 3],
							borderWidth: 1,
						},
					],
				},
				options: {
					maintainAspectRatio: false,
					legend: {
						display: false,
					},
					scales: {
						xAxes: [{ ticks: { suggestedMax: 10 }, stacked: true }],
						yAxes: [{ stacked: true }],
					},
					tooltips: {
						enabled: false,
						custom: customTooltips,
					},
				},
			})
		} else {
			ctxMount.current.update()
		}

		return function cleanup() {
			ctxMount.current.destroy()
			ctxMount.current = null
		}
	}, [customTooltips, store.count])

	const canvasJsx = useMemo(() => <canvas ref={canvasEl}></canvas>, [])

	const tooltipJsx = useMemo(
		() => (
			<div
				id="chartjs-tooltip"
				style={tooltip.style}
				className={tooltip.className}
				dangerouslySetInnerHTML={{ __html: tooltip.body }}
			></div>
		),
		[tooltip]
	)

	return (
		<>
			<Box p={1} textAlign="center" fontWeight="fontWeightMedium" fontSize="body2.fontSize">
				{format(store.date, 'yyyy년 MM월 dd일')}의 시간별 박수
			</Box>
			<Box className={classes.chart}>
				{canvasJsx}
				{tooltipJsx}
			</Box>
		</>
	)
}

export default observer(ClapChart)
